// ─── Recurring cybo schedules (#611/#613/#619) ───────────────────────────────
// Client state for the per-workspace list of recurring schedules (the Tasks-tab
// "Scheduled" board). A schedule belongs to a CYBO and runs it with a prompt on a
// cron cadence; the clock lives on the daemon (ScheduleRunner). This state only
// does CRUD + reads run history, and stays live via the `cyborg:schedule_mutated`
// broadcast (wired in app.svelte.ts, the single place client.on(...) handlers live).
//
// Rows are sorted soonest-next-run first (disabled/no-next sink to the bottom).

import { client } from "./client.js";
import { localTimeZone } from "../logs/timeline/time.js";
import type { ScheduleView, ScheduleRunView, ScheduleMutatedPayload } from "../ws-client.js";

// A check-back (cyborg7_check_back) is a one-shot whose prompt starts with the server's round mark.
// Shown as "Check back · <why> · <local time>", never the raw prompt and its UTC day-of-month cron.
// A display hint only: the server counts rounds and caps from its own rows, never from this.
const CHECK_BACK_MARK = /^\[check-back [12]\/2\] You set this to come back to: (.*)/;

export function checkBackWhy(prompt: string | null | undefined): string | null {
  const why = CHECK_BACK_MARK.exec(prompt ?? "")?.[1]?.trim();
  return why ? why : null; // null: not a check-back
}

/** "Thu 11:55 PM" in this browser's zone. */
export function checkBackTime(ts: number): string {
  return new Date(ts).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
}

export function checkBackTitle(
  s: Pick<ScheduleView, "prompt" | "nextRunAt" | "lastRunAt">,
): string | null {
  const why = checkBackWhy(s.prompt);
  if (why === null) return null; // null: not a check-back
  const at = s.nextRunAt ?? s.lastRunAt;
  return at ? `Check back · ${why} · ${checkBackTime(at)}` : `Check back · ${why}`;
}

/** A check-back that ran (or was switched off): off the default list, kept under Finished. */
export function isFinishedCheckBack(
  s: Pick<ScheduleView, "prompt" | "enabled" | "pausedReason">,
): boolean {
  return checkBackWhy(s.prompt) !== null && !s.enabled && s.pausedReason == null;
}

// The relay's SCHEDULE_RUNS_BATCH_MAX_IDS (relay-standalone.ts).
const RUNS_BATCH_MAX_IDS = 200;

function byNextRun(a: ScheduleView, b: ScheduleView): number {
  // Soonest next run first; schedules with no next run (disabled / exhausted)
  // sink below the active ones, then tie-break by creation order for stability.
  const an = a.nextRunAt ?? Number.POSITIVE_INFINITY;
  const bn = b.nextRunAt ?? Number.POSITIVE_INFINITY;
  if (an !== bn) return an - bn;
  return a.createdAt - b.createdAt;
}

export class SchedulesState {
  // The loaded schedules for `workspaceId`, soonest-next-run first. Reassigned on
  // every mutation so the runes observe the change.
  list: ScheduleView[] = $state([]);
  loading = $state(false);
  // Human-readable load error (null = none) — "the LIST couldn't load".
  error: string | null = $state(null);
  // Run history keyed by scheduleId (lazy-loaded when a row's drawer opens).
  runsBySchedule: Record<string, ScheduleRunView[]> = $state({});
  // The relay and home machines can continue a session (the CLI's --continue-session preflight).
  supportsContinueSession = $state(false);

  // Which workspace the current `list` belongs to — guards a late response from
  // landing in a workspace the user has since switched away from.
  private loadedWorkspaceId: string | null = null;
  private inflight: Promise<void> | null = null; // null: no load in flight
  // Which workspace we last ATTEMPTED to load (success OR failure). ensureLoaded()
  // reads this to load exactly once per workspace, so a reactive $effect can't spin
  // load() forever when the result is legitimately empty or the fetch keeps failing.
  private attemptedWorkspaceId: string | null = $state(null);

  get enabledCount(): number {
    return this.list.reduce((n, s) => n + (s.enabled ? 1 : 0), 0);
  }

  // Load (or reload) the workspace's schedules. Safe to call repeatedly.
  async load(workspaceId: string): Promise<void> {
    const run = this.fetchList(workspaceId);
    this.inflight = run;
    try {
      await run;
    } finally {
      if (this.inflight === run) this.inflight = null; // null: no load in flight
    }
  }

  // The agent a schedule runs, loading the list first when it is not there yet (a notice click can
  // land before any surface loaded it); an in-flight load is waited for, not duplicated.
  async cyboIdOf(workspaceId: string, scheduleId: string): Promise<string | undefined> {
    const find = () => this.list.find((s) => s.id === scheduleId)?.cyboId;
    const known = find();
    if (known) return known;
    if (this.inflight) await this.inflight;
    else if (this.attemptedWorkspaceId !== workspaceId) await this.load(workspaceId);
    return find();
  }

  private async fetchList(workspaceId: string): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const { schedules, supportsContinueSession } = await client.listSchedules(workspaceId);
      this.loadedWorkspaceId = workspaceId;
      this.supportsContinueSession = supportsContinueSession === true;
      this.list = [...schedules].sort(byNextRun);
    } catch (e) {
      // intentional: the message lands in `error`, which the schedule views render
      this.error = e instanceof Error ? e.message : "Couldn't load schedules";
      // A reload that fails keeps what this workspace already showed.
      if (this.loadedWorkspaceId !== workspaceId) this.list = [];
    } finally {
      this.loading = false;
      // Mark attempted regardless of outcome so ensureLoaded() won't auto-retry an
      // empty or failing load in a loop. A workspace switch (different id) re-arms it.
      this.attemptedWorkspaceId = workspaceId;
    }
  }

  // Idempotent load for reactive callers (a $effect on mount). Loads at most ONCE
  // per workspace — unlike load(), it will NOT re-fire on an empty result or a
  // persistent error, so it can't spin the "Loading…" state forever. Explicit
  // refresh paths (an explicit reload, a mutation) still call load() directly.
  async ensureLoaded(workspaceId: string): Promise<void> {
    if (this.attemptedWorkspaceId === workspaceId || this.loading) return;
    await this.load(workspaceId);
  }

  // Insert/replace a row (from a create/update/set_enabled ack or a broadcast)
  // without a full reload. Scoped to the loaded workspace.
  upsert(schedule: ScheduleView): void {
    if (this.loadedWorkspaceId !== null && schedule.workspaceId !== this.loadedWorkspaceId) return;
    const next = this.list.filter((s) => s.id !== schedule.id);
    next.push(schedule);
    this.list = next.sort(byNextRun);
  }

  // Drop a row by id (from a delete ack or a broadcast).
  remove(scheduleId: string): void {
    this.list = this.list.filter((s) => s.id !== scheduleId);
    const { [scheduleId]: _dropped, ...rest } = this.runsBySchedule;
    this.runsBySchedule = rest;
  }

  // Apply a `cyborg:schedule_mutated` broadcast/ack to keep the board live. A
  // create/update/set_enabled carries the row; delete drops it; run_once leaves
  // the list shape unchanged (run history is reloaded on demand).
  applyMutation(payload: ScheduleMutatedPayload): void {
    if (!payload.ok) return;
    if (payload.op === "delete") {
      if (payload.scheduleId) this.remove(payload.scheduleId);
      return;
    }
    if (payload.schedule) this.upsert(payload.schedule);
  }

  // ── Mutations ──────────────────────────────────────────────────────────
  // Each resolves with the server's mutation ack; on success it patches `list`
  // locally so the UI updates without waiting for the broadcast round-trip.

  async create(params: {
    workspaceId: string;
    cyboIdOrSlug: string;
    cron: string;
    prompt: string;
    destination?: "channel" | "dm";
    channelId?: string | null;
    taskId?: string | null;
    continueSession?: string | null;
    timezone?: string | null;
    maxRuns?: number | null;
  }): Promise<ScheduleMutatedPayload> {
    // The cron is the user's wall clock: with no zone the server reads it as UTC.
    const res = await client.createSchedule({
      ...params,
      timezone: params.timezone ?? localTimeZone(),
    });
    if (res.ok) this.applyMutation(res);
    return res;
  }

  async update(params: {
    workspaceId: string;
    scheduleId: string;
    cron?: string;
    prompt?: string;
    destination?: "channel" | "dm";
    channelId?: string | null;
    taskId?: string | null;
    continueSession?: string | null;
    timezone?: string | null;
  }): Promise<ScheduleMutatedPayload> {
    const res = await client.updateSchedule(params);
    if (res.ok) this.applyMutation(res);
    return res;
  }

  async setEnabled(
    workspaceId: string,
    scheduleId: string,
    enabled: boolean,
  ): Promise<ScheduleMutatedPayload> {
    const res = await client.setScheduleEnabled(workspaceId, scheduleId, enabled);
    if (res.ok) this.applyMutation(res);
    return res;
  }

  async delete(workspaceId: string, scheduleId: string): Promise<ScheduleMutatedPayload> {
    const res = await client.deleteSchedule(workspaceId, scheduleId);
    if (res.ok) this.applyMutation(res);
    return res;
  }

  async runOnce(workspaceId: string, scheduleId: string): Promise<ScheduleMutatedPayload> {
    return client.runScheduleOnce(workspaceId, scheduleId);
  }

  // Load (or reload) a schedule's run history into runsBySchedule. Returns the
  // error string on failure so the caller can surface it inline. The newest 20 merge into
  // what is already held, so a detail view never drops deeper history a batch seeded.
  async loadRuns(workspaceId: string, scheduleId: string): Promise<string | null> {
    try {
      const { runs } = await client.listScheduleRuns(workspaceId, scheduleId, 20);
      const fresh = new Set(runs.map((r) => r.id));
      const kept = (this.runsBySchedule[scheduleId] ?? []).filter((r) => !fresh.has(r.id));
      const merged = [...runs, ...kept].sort((a, b) => b.startedAt - a.startedAt);
      this.runsBySchedule = { ...this.runsBySchedule, [scheduleId]: merged };
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : "Couldn't load run history";
    }
  }

  // Seed several schedules' run history in one call (catchUp). Once seeded, applyRun
  // keeps each history live. Rejects on failure so the caller decides how to report it.
  // The relay refuses a batch over RUNS_BATCH_MAX_IDS ids, so a big workspace goes in chunks.
  async loadRunsBatch(workspaceId: string, ids: readonly string[], limit = 50): Promise<void> {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += RUNS_BATCH_MAX_IDS)
      chunks.push(ids.slice(i, i + RUNS_BATCH_MAX_IDS));
    const replies = await Promise.all(
      chunks.map((c) => client.listScheduleRunsBatch(workspaceId, c, limit)),
    );
    if (this.loadedWorkspaceId !== null && this.loadedWorkspaceId !== workspaceId) return;
    for (const { runs } of replies) this.runsBySchedule = { ...this.runsBySchedule, ...runs };
  }

  // After a socket drop the broadcasts sent meanwhile are gone: re-read what is already held.
  async catchUp(workspaceId: string): Promise<void> {
    if (this.attemptedWorkspaceId !== workspaceId) return;
    await this.load(workspaceId);
    const held = this.list
      .filter((s) => s.workspaceId === workspaceId && s.id in this.runsBySchedule)
      .map((s) => s.id);
    if (held.length > 0) await this.loadRunsBatch(workspaceId, held);
  }

  // Apply a live `cyborg:schedule_run_recorded` broadcast — upsert the run into an
  // ALREADY-loaded history so an open detail reflects a fire/finish live. If the
  // schedule's history isn't loaded, ignore it: seeding a partial list from one
  // broadcast would look complete when it isn't; the next loadRuns fetches the full
  // history. Kept newest-first to match listScheduleRuns.
  applyRun(run: ScheduleRunView): void {
    const existing = this.runsBySchedule[run.scheduleId];
    if (!existing) return;
    const next = [run, ...existing.filter((r) => r.id !== run.id)].sort(
      (a, b) => b.startedAt - a.startedAt,
    );
    this.runsBySchedule = { ...this.runsBySchedule, [run.scheduleId]: next };
  }

  // Drop all state on logout / workspace teardown.
  clear(): void {
    this.list = [];
    this.loading = false;
    this.error = null;
    this.runsBySchedule = {};
    this.supportsContinueSession = false;
    this.loadedWorkspaceId = null;
    this.attemptedWorkspaceId = null;
  }
}

export const schedulesState = new SchedulesState();
